import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { changedField, fieldBorder, focusRing, hoverable } from '../../interaction-states';
import { typographyVariantStyles } from '../../typography-variants';
import type { FileUploadStatus } from './types';

function getRowBorderColor(theme: Theme, status: FileUploadStatus): string {
  return status === 'error' ? theme.colors.error.border : theme.colors.border.default;
}

export const StyledRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[16],
  width: '100%',
}));

export const StyledFileList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  width: '100%',
}));

export const StyledItemFrame = styled('div')(({ theme }) => ({
  ...changedField(theme),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  width: '100%',
  boxSizing: 'border-box',
  padding: theme.spacing[16],
  borderRadius: theme.radius[16],
  border: `2px dashed ${fieldBorder(theme.colors.border.default)}`,
  backgroundColor: theme.colors.background.raised,
}));

const ROW_TRANSIENT_PROPS = new Set(['$status']);

export const StyledFileRow = styled('div', {
  shouldForwardProp: (prop) => !ROW_TRANSIENT_PROPS.has(prop as string),
})<{ $status: FileUploadStatus }>(({ theme, $status }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[16],
  width: '100%',
  boxSizing: 'border-box',
  padding: theme.spacing[16],
  borderRadius: theme.radius[16],
  border: `1px solid ${getRowBorderColor(theme, $status)}`,
  backgroundColor: theme.colors.background.raised,
}));

export const StyledFileIconWrapper = styled('span')({
  display: 'inline-flex',
  flexShrink: 0,

  '& svg': {
    width: 32,
    height: 32,
  },
});

export const StyledImagePreview = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: theme.spacing[32],
  height: theme.spacing[32],
  overflow: 'hidden',
  borderRadius: theme.radius[8],
  backgroundColor: theme.colors.neutral.subtle,
  color: theme.colors.icon.secondary,

  '& svg': {
    width: 16,
    height: 16,
  },
}));

export const StyledPreviewImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export const StyledFileInfo = styled('div', {
  shouldForwardProp: (prop) => prop !== '$inline',
})<{ $inline?: boolean }>(({ theme, $inline }) => ({
  display: 'flex',
  flexDirection: $inline ? 'row' : 'column',
  alignItems: $inline ? 'center' : 'stretch',
  justifyContent: 'center',
  gap: $inline ? theme.spacing[8] : 0,
  flex: 1,
  minWidth: 0,

  ...($inline && {
    '& > :first-child': {
      flex: 1,
      minWidth: 0,
    },
  }),
}));

export const StyledFileName = styled('span')(({ theme }) => ({
  ...typographyVariantStyles.body2,
  fontWeight: 500,
  color: theme.colors.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const StyledFileNameButton = styled('button')(({ theme }) => ({
  ...typographyVariantStyles.body2,
  ...hoverable(theme, 'underline'),
  ...focusRing(theme, { inset: false }),
  fontWeight: 500,
  color: theme.colors.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textAlign: 'left',
  minWidth: 0,
  padding: 0,
  border: 0,
  borderRadius: theme.radius[4],
  background: 'none',
  cursor: 'pointer',
}));

export const StyledFileMeta = styled('span')(({ theme }) => ({
  ...typographyVariantStyles.body3,
  color: theme.colors.text.secondary,
  flexShrink: 0,
  whiteSpace: 'nowrap',
}));

export const StyledActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  flexShrink: 0,
}));
